<template>
  <div>
    <div class="d-flex justify-content-between">
      <h1 class="page-header">{{ $t('customerMemberCard.pageTitle') }}</h1>
      <ts-breadcrumb :items="[
        { text: $t('home'), href: '/' },
        {
          text: $t('customerMemberCard.pageTitle'),
          active: true
        }
      ]" />
    </div>
    <ts-card>
      <ts-card-wrapper>
        <div class="d-flex justify-content-between">
          <ts-button outline color="info" @click.prevent="addNew">{{
            $t('add_new')
          }}</ts-button>
          <div class="d-flex ts-space-x-3 ts-items-center">
            <a-select v-model="status_id" :options="statusList" option-filter-prop="children" style="width: 120px"
              placeholder="Select"></a-select>
            <ts-input v-model="search" :placeholder="$t('search')" style="width: 200px" class="inline-block" />
          </div>
        </div>
      </ts-card-wrapper>
      <Table :columns="columns" :data="resources" size="small" :loading="loading" highlight-row stripe
        max-height="700">
        <template slot-scope="{ row }" slot="customer_name">
          {{ row.customer_name }}
          <i class="fa fa-sticky-note text-warning ml-1" v-if="row.remark" v-tooltip="row.remark"></i>
        </template>
        <template slot-scope="{ row }" slot="status">
          <span class="badge badge-success" v-if="row.status_id == 1">
            {{ row.status_name }}
          </span>
          <span class="badge badge-warning" v-else-if="row.status_id == 2">
            {{ row.status_name }}
          </span>
          <span class="badge badge-secondary" v-else-if="row.status_id == 3">
            {{ row.status_name }}
          </span>
          <span class="badge badge-danger" v-else>
            {{ row.status_name }}
          </span>
        </template>
        <template slot-scope="{ row }" slot="promotion_type">
          <span class="badge badge-primary" v-if="row.promotion_type_id === 1">{{ $t('customerMemberCard.normal')
          }}</span>
          <span class="badge badge-info" v-else-if="row.promotion_type_id === 2">{{ $t('customerMemberCard.package')
          }}</span>
          <span v-else>-</span>
        </template>
        <template slot-scope="{ row }" slot="item">
          <span v-if="row.promotion_type_id === 1">{{ row.card_types || '-' }}</span>
          <span v-else-if="row.promotion_type_id === 2">{{ row.package_name || '-' }}</span>
          <span v-else>-</span>
        </template>
        <template slot-scope="{ row }" slot="usage">
          <span v-tooltip="`${$t('customerMemberCard.usage')}: ${row.used_times || 0} / ${row.max_times}`">
            {{ row.available_times != null ? row.available_times : row.max_times - (row.used_times || 0) }} / {{
              row.max_times }}
          </span>
        </template>
        <template slot-scope="{ row }" slot="discount">
          <span v-if="row.discount_rate || row.discount_amount">
            {{ row.discount_type === '$' ? `$${row.discount_amount}` : `${row.discount_rate}%` }}
          </span>
          <span v-else>-</span>
        </template>
        <template slot-scope="{ row }" slot="validity">
          {{ row.activate_date }} <i class="fa fa-arrow-right mx-1"></i> {{ row.expiry_date ? row.expiry_date : 'N/A' }}
        </template>
        <template slot-scope="{ row }" slot="action">
          <a href="#" v-tooltip="$t('customerMemberCard.assignCustomer')" class="text-success mr-2"
            @click.prevent="assignCustomer(row)" v-if="row.status_id == 3">
            <i class="fa fa-user-plus"></i>
          </a>
          <a href="#" v-tooltip="$t('customerMemberCard.generateInvoice')" class="text-success mr-2"
            @click.prevent="generateInvoice(row)" v-if="row.customer_id && !row.ref_stock_out_id">
            <i class="fas fa-file-invoice-dollar"></i>
          </a>
          <a href="#" v-tooltip="$t('edit')" class="text-primary mr-2" @click.prevent="edit(row)"
            v-if="!row.ref_stock_out_id">
            <i class="fas fa-edit"></i>
          </a>
          <a v-else class="text-muted mr-2" style="cursor: not-allowed; opacity: 0.5;" v-tooltip="$t('edit')">
            <i class="fas fa-edit"></i>
          </a>
          <a-popconfirm v-if="!row.ref_stock_out_id" placement="leftTop" :title="$t('are_you_sure_to_delete')"
            :ok-text="$t('yes')" :cancel-text="$t('no')" @confirm="deleteRecord(row)">
            <a href="#" v-tooltip="$t('delete')" :disabled="row._deleting">
              <i class="fas fa-circle-notch spin" v-if="row._deleting"></i>
              <i class="fa fa-trash text-red" v-else></i>
            </a>
          </a-popconfirm>
          <a v-else class="text-muted" style="cursor: not-allowed; opacity: 0.5;" v-tooltip="$t('delete')">
            <i class="fa fa-trash"></i>
          </a>
        </template>
      </Table>
      <div class="d-flex justify-content-between align-items-center p-15">
        <ts-total-list v-model="pagination" />
        <ts-pagination v-model="pagination" @navigate="fetchData"></ts-pagination>
      </div>
      <!-- form-addnew -->
      <a-modal v-if="showForm" v-model="showForm" :title="$t('customerMemberCard.pageTitle')" centered
        @cancel="clearEdit" :zIndex="1040" :footer="null" width="800px">
        <from-action @cancel="clearEdit" @fetchData="fetchData" />
      </a-modal>
      <!-- form-update-status -->
      <a-modal v-if="showFormUpdate" v-model="showFormUpdate" :title="$t('customerMemberCard.pageTitle')" centered
        @cancel="clearEdit" :zIndex="1040" :footer="null" width="800px">
        <from-change-status @cancel="clearEdit" @fetchData="fetchData" />
      </a-modal>
      <!-- form-assign-customer -->
      <a-modal v-if="showFormAssign" v-model="showFormAssign" :closable="false" centered @cancel="clearEdit"
        :zIndex="1040" :footer="null" :body-style="{ padding: 0 }" width="620px" :maskClosable="false">
        <assign-customer @cancel="clearEdit" @assigned="onAssigned" />
      </a-modal>
    </ts-card>
  </div>
</template>

<script>
import { debounce } from 'lodash'
import { mapActions, mapState } from 'vuex'
import FromAction from './form'
import FromChangeStatus from './form-change-status'
import AssignCustomer from './assign-customer.vue'
import expandRow from './table-expand.vue'
export default {
  name: 'customerMemberCardIndex',
  components: {
    FromAction,
    FromChangeStatus,
    AssignCustomer
  },
  data() {
    return {
      loading: false,
      showForm: false,
      showFormUpdate: false,
      showFormAssign: false,
      status_id: 1,
    }
  },
  created() {
    this.fetchData()
    this.getFormViewData({ params: undefined, index: undefined })
  },
  computed: {
    ...mapState('customer/customerMemberCard', ['resources', 'pagination', 'formViewData', 'edit_data']),
    search: {
      get() {
        return this.$store.state.customer.customerMemberCard.search
      },
      set(newValue) {
        this.$store.commit('customer/customerMemberCard/SET_SEARCH', newValue)
        this.$store.commit('customer/customerMemberCard/RESET_CURRENT_PAGE')
      }
    },
    columns() {
      return [
        {
          type: 'expand',
          render: (h, params) => {
            return h(expandRow, {
              props: {
                records: params.row.details
              },
              on: { cancel: this.fetchData }
            })
          },
          width: 30
        },
        {
          title: this.$t('customerMemberCard.customerName'),
          slot: 'customer_name',
          width: 190
        },
        {
          title: this.$t('customerMemberCard.serialCard'),
          key: 'serial_card',
          width: 140
        },
        {
          title: this.$t('customerMemberCard.status'),
          slot: 'status',
          align: 'center',
          width: 110
        },
        {
          title: this.$t('customerMemberCard.gender'),
          key: 'gender',
          align: 'center',
          width: 90
        },
        {
          title: this.$t('customerMemberCard.contactNumber'),
          key: 'contact_number',
          align: 'center',
          width: 160
        },
        {
          title: this.$t('customerMemberCard.promotionType'),
          slot: 'promotion_type',
          align: 'center',
          width: 150
        },
        {
          title: this.$t('customerMemberCard.item'),
          slot: 'item',
          width: 180
        },
        {
          title: this.$t('customerMemberCard.usage'),
          slot: 'usage',
          align: 'center',
          width: 110
        },
        {
          title: this.$t('customerMemberCard.discount'),
          slot: 'discount',
          align: 'center',
          width: 100
        },
        {
          title: this.$t('customerMemberCard.validity'),
          slot: 'validity',
          align: 'center',
          width: 240
        },
        {
          title: this.$t('customerMemberCard.createdDate'),
          key: 'created_date',
          minWidth: 180
        },
        {
          title: this.$t('actions'),
          key: 'action',
          slot: 'action',
          fixed: 'right',
          align: 'center',
          className: 'ts-space-x-1',
          width: 120
        }
      ]
    },
    statusList() {
      return [
        { label: 'Active', value: 1 },
        { label: 'Inactive', value: 2 },
        { label: 'Pending', value: 3 },
        { label: 'All', value: 0 }
      ]
    },
  },
  methods: {
    ...mapActions('customer/customerMemberCard', ['getFormViewData']),
    fetchData(attributes) {
      this.loading = true
      this.$store
        .dispatch('customer/customerMemberCard/fetch', {
          status_id: this.status_id,
          ...attributes
        })
        .then(response => {
          this.loading = false
        })
        .catch(error => {
          this.loading = false
          this.$toasted.error(error.message)
        })
    },
    addNew() {
      this.showForm = true
    },
    edit(record) {
      this.$store.commit('customer/customerMemberCard/SET_EDIT_DATA', record)
      this.showForm = true
    },
    clearEdit() {
      this.$store.commit('customer/customerMemberCard/SET_EDIT_DATA', {})
      this.showForm = false
      this.showFormUpdate = false
      this.showFormAssign = false
      this.fetchData()
    },
    assignCustomer(record) {
      this.$store.commit('customer/customerMemberCard/SET_EDIT_DATA', record)
      this.showFormAssign = true
    },
    generateInvoice(record) {
      this.$store.commit('customer/customerMemberCard/SET_EDIT_DATA', record)
      this.showFormAssign = true
    },
    onAssigned() {
      this.clearEdit()
    },
    deleteRecord(record) {
      record._deleting = true
      this.$store
        .dispatch(
          'customer/customerMemberCard/destroy',
          record.customer_member_card_id
        )
        .then(response => {
          this.$toasted.success(response.message)
          this.fetchData()
        })
        .catch(error => {
          this.$toasted.error(error.message)
        })
        .finally(() => {
          record._deleting = false
        })
    },
    branchFilter(value) {
      this.$store.commit('customer/customerMemberCard/SET_BRANCH_ID', value)
      this.fetchData()
    }
  },
  watch: {
    search: debounce(function (search) {
      this.fetchData()
    }, 500),
    status_id: debounce(function () {
      this.fetchData()
    }, 500)
  },
  beforeRouteLeave(to, from, next) {
    this.$store.commit('customer/customerMemberCard/RESET_STATE')
    next()
  }
}
</script>
